import { useState } from "react";
import { useLocation } from "wouter";
import {
  useListContacts,
  useListAccounts,
  useListInventoryPlans,
  type ListContactsActive,
} from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ListPagination } from "@/components/common/list-pagination";
import { MemberDirectoryTable } from "@/components/members/member-directory-table";

// Phase 37-04: person-first Member directory page — the "who" view that
// complements the account-first /accounts list. Mirrors accounts.tsx's shell
// (search Input + filter Selects + hide-test-data Switch + ListPagination
// over a server-filtered set): fetch page:1,pageSize:200 from the extended
// GET /contacts (37-02), then apply the client-side hide-test-data filter +
// client-side pagination, same pattern as AccountsPage.
//
// NOT yet routed/navigable — App.tsx wiring + sidebar nav land in 37-06. Row
// click already targets /members/:contactId per that plan's contract.
export default function MemberDirectory() {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<"all" | number>("all");
  const [activeFilter, setActiveFilter] = useState<"all" | ListContactsActive>(
    "all",
  );
  const [accountFilter, setAccountFilter] = useState<"all" | number>("all");
  const [hasAttendanceFilter, setHasAttendanceFilter] = useState(false);
  const [hideTestData, setHideTestData] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Same rationale as AccountsPage: "hide test data" + pagination are
  // client-side, so we fetch the full server-filtered result set (capped at
  // 200) rather than letting the server paginate ahead of the test-data
  // filter.
  const params = {
    q: search || undefined,
    accountId: accountFilter === "all" ? undefined : accountFilter,
    planId: planFilter === "all" ? undefined : planFilter,
    active: activeFilter === "all" ? undefined : activeFilter,
    hasAttendance: hasAttendanceFilter ? true : undefined,
    page: 1,
    pageSize: 200,
  };

  const { data: contacts, isLoading } = useListContacts(params, {
    query: { queryKey: ["/api/contacts", params] },
  });

  const { data: accounts } = useListAccounts(
    { page: 1, pageSize: 200 },
    { query: { queryKey: ["/api/accounts", { page: 1, pageSize: 200 }] } },
  );
  const { data: plans } = useListInventoryPlans();

  const allItems = contacts ?? [];
  const filtered = hideTestData
    ? allItems.filter((c) => !/^Test /i.test(c.accountLegalName))
    : allItems;
  const hiddenTestCount = allItems.length - filtered.length;

  // Rows already arrive sorted server-side by recent activity (37-02); do
  // not re-sort client-side.
  const totalFiltered = filtered.length;
  const pageCount = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * pageSize;
  const items = filtered.slice(pageStart, pageStart + pageSize);

  function handleSearch(v: string) {
    setSearch(v);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Members</h1>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search by name, phone, or account…"
          className="w-[260px]"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Select
          value={planFilter === "all" ? "all" : String(planFilter)}
          onValueChange={(v) => {
            setPlanFilter(v === "all" ? "all" : Number(v));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All plans" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            {(plans ?? []).map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.planName.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={activeFilter}
          onValueChange={(v) => {
            setActiveFilter(v as "all" | ListContactsActive);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={accountFilter === "all" ? "all" : String(accountFilter)}
          onValueChange={(v) => {
            setAccountFilter(v === "all" ? "all" : Number(v));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            {(accounts ?? []).map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.legalName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            id="has-attendance"
            checked={hasAttendanceFilter}
            onCheckedChange={(v) => {
              setHasAttendanceFilter(v);
              setPage(1);
            }}
            data-testid="member-directory-has-attendance-switch"
          />
          <Label htmlFor="has-attendance" className="text-xs cursor-pointer">
            Has attendance
          </Label>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Switch
            id="hide-test-data"
            checked={hideTestData}
            onCheckedChange={(v) => {
              setHideTestData(v);
              setPage(1);
            }}
            data-testid="member-directory-hide-test-switch"
          />
          <Label htmlFor="hide-test-data" className="text-xs cursor-pointer">
            Hide test data
            {hideTestData && hiddenTestCount > 0 && (
              <span className="ml-1 text-muted-foreground">
                ({hiddenTestCount} hidden)
              </span>
            )}
          </Label>
        </div>
      </div>

      <MemberDirectoryTable
        rows={items}
        isLoading={isLoading}
        onRowClick={(id) => navigate(`/members/${id}`)}
      />

      {!isLoading && totalFiltered > 0 && (
        <ListPagination
          total={totalFiltered}
          page={safePage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          isLoading={isLoading}
          noun={["member", "members"]}
        />
      )}
    </div>
  );
}
